import {
  Button, Card,
  CardMedia, TextField,
  Grid, Grow,
  Icon, IconButton,
  LinearProgress,
  Typography,
  Dialog
} from '@material-ui/core';
import { format } from 'date-fns';
import { makeStyles } from '@material-ui/core/styles';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { getMetadataAPI, getMyLibraryAPI, createPromoByTemplateAPI, deleteSingleTemplateAPI } from 'api';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CustomToolbarSelect, ConfirmDialog } from 'components';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DateFnsUtils from '@date-io/date-fns';
import { useHistory, Link } from 'react-router-dom';
import MUIDataTable from 'mui-datatables';
import CircularProgress from '@material-ui/core/CircularProgress';
import MarkdownIt from 'markdown-it';
import noImage from 'components/Logo/no-image.png';
import logo from '../../components/Logo/logo-dermpro.svg';
import { discount_unit } from '../../utils';


const md = new MarkdownIt();


const useStyles = makeStyles(() => ({
  custominput: {
    '& > div.MuiInputBase-root': {
      '& .MuiOutlinedInput-input.MuiInputBase-input': {
        padding: '6px 14px',
      }
    },
    maxWidth: '300px',
    width: '100%',
  },
  modal: {
    minWidth: '768px',
    maxWidth: '768px'
  },
  category: {
    cursor: 'pointer',
  },
  imageblock: {
    height: '240px',
  },
  cardImage: {
    objectFit: 'scale-down',
  },
  cardBackground: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    height: '240px',
  },
  description: {
    maxHeight: '60px',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  content: {
    position: 'relative',
    margin: '20px 0'
  },
  datesRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  dateInput: {
    margin: 10
  },
  startButton: {
    margin: '20px 10px 10px 10px'
  },
  labelsFilter: {
    minWidth: 400,
  },
}));

export default function Library({tabName}) {
  const classes = useStyles();
  const history = useHistory();

  const dispatch = useDispatch();
  const {myLibraryData = [], totalCount = 0, page = 1, libraryLoading, error} = useSelector((state) => state.library);
  const {metadata = {}, loadingPromotionsList} = useSelector((state) => state.promo);

  const labelOptions = Object.entries(metadata)?.reduce((acc, [, labels]) => {
    acc = [...acc, ...labels];
    return acc;
  }, []);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [activeTemplate, setActiveTemplate] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filter, setFilter] = useState({page: 1, limit: 20, name: '', sorting: 'desc', labelsIds: ''});
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    getMetadataAPI({dispatch});
  }, []);

  useEffect(() => {
    dispatch(getMyLibraryAPI({...filter}));
  }, [filter]);

  const handleClickStart = async (template) => {
    handleCloseDialog();

    if (template._id === activeTemplate._id) {
      const success = await createPromoByTemplateAPI({
        dispatch,
        id: template._id,
        startDate: format(startDate, 'MM/dd/yyyy'),
        endDate: format(endDate, 'MM/dd/yyyy'),
      });

      if (success) {
        history.push('/my-promotions');
      }
    }
  };

  const handleCloseDialog = () => {
    setActiveTemplate({});
  };

  const handleFilterSubmit = (filterList) => {
    const labelsIds = filterList[3]?.map((label) => label._id);
    setFilter((filter) => ({...filter, labelsIds}));
  };

  const columns = [
    {
      name: 'banner', // field name in the row object
      label: 'Banner', // column title that will be shown in table
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: () => ({className: 'w-300'}),
        customBodyRenderLite: (dataIndex) => {
          const item = myLibraryData[dataIndex];

          return (
            <div className="flex items-center max-w-250 w-full">
              <img
                src={(loaded && item.slider.bannerImageInfo?.imageS3) ? item.slider.bannerImageInfo?.imageS3 : noImage}
                alt={item.promoName}
                className="w-full cursor-pointer"
                onLoad={() => setLoaded(true)}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = noImage;
                }}
                onClick={() => history.push({
                  pathname: `/my-promotions/${tabName}/template-details/${item._id}`,
                  state: { promo: item }
                })}
              />
            </div>
          );
        }
      },
    },
    {
      name: 'promoName', // field name in the row object
      label: 'Promotion Title', // column title that will be shown in table
      options: {
        sort: false,
        filter: false,
        filterOptions: {
          fullWidth: true
        }
      },
    },
    {
      name: 'action',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ style: { width: '120px'}, className: 'text-14'}),
        customBodyRenderLite: (dataIndex) => {
          const template = myLibraryData[dataIndex] || {};
          const isLoadingPromo = !!loadingPromotionsList.find((el) => el === template._id);
          return (
            isLoadingPromo ?
              <CircularProgress size={25} />
              :
              <>
                <IconButton
                  color="primary"
                  variant="contained"
                  title="Edit"
                  disabled={activeTemplate?._id === template._id && (endDate < startDate)}
                  onClick={() => {
                    setActiveTemplate(template);
                  }}
                >
                  <Icon className="text-gray">insert_invitation</Icon>
                </IconButton>
                <IconButton
                  title="Click here to delete "
                  onClick={() => setTemplateToDelete(template)}
                >
                  <Icon className="text-gray">delete</Icon>
                </IconButton>
              </>
          );
        },
      },
    },
    {
      name: 'labels',
      label: 'Labels',
      options: {
        filter: true,
        display: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const item = myLibraryData[dataIndex];
          const uniqueLabels = [...new Set(item.labels.map((item) => item.label))].join(', ') || '';
          return (
            <div className="flex items-center">
              <p className="my-0 text-15" >{uniqueLabels}</p>
            </div>
          );
        },
        filterType: 'custom',
        customFilterListOptions: {
          render: (v) => {
            if (v[0] === '') {
              v = [];
            }

            return `${v[0].type}: ${v[0].label}`;
          },
          update: (filterList, _filterPos, index) => {
            filterList[index] = [];
            setFilter((filter) => ({...filter, labelsIds: ''}));
            return filterList;
          }
        },
        filterOptions: {
          fullWidth: true,
          display: (filterList, onChange, index, column) => {
            return (
              <Autocomplete
                displayEmpty
                className={classes.labelsFilter}
                value={labelOptions.find((label) => filter.labelsIds.includes(label._id)) || ''}
                options={labelOptions}
                getOptionLabel={(option) => option?.label || ''}
                groupBy={(option) => option?.type}
                onChange={(_, value, action) => {
                  if (action === 'clear') {
                    filterList[index] = [];
                    onChange(filterList[index], index, column);
                    setFilter((filter) => ({...filter, labelsIds: ''}));
                  } else if (action === 'select-option') {
                    filterList[index] = [value];
                    onChange(filterList[index], index, column);
                    setFilter((filter) => ({...filter, labelsIds: value._id}));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    name="Labels"
                    label="Labels"
                  />
                )}
              />
            );
          }
        }
      }
    },
  ];

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y">
      {!libraryLoading && error && <div>Something went wrong</div>}
      {libraryLoading && !error && <LinearProgress className="m-10" />}
      <MUIDataTable
        data={myLibraryData}
        columns={columns}
        options={{
          filterArrayFullMatch: false,
          filterType: 'textField',
          responsive: 'standard',
          selectableRows: 'none', // set checkbox for each row
          search: true, // set search option
          filter: true, // set data filter option
          download: false, // set download option
          print: false, // set print option
          pagination: true, //set pagination option
          viewColumns: true, // set column option
          textLabels: {
            body: {
              noMatch:
              (
                <Link to="/promotions-library/browse-promotion" underline="none">
                  <Button
                    className="uppercase custom-check-all custom-check-all-select mr-2"
                    variant="contained"
                    color="primary"
                    underline="none"
                  >
                    Search from Library
                  </Button>
                </Link>
              )
            }
          },
          elevation: 0,
          searchOpen: Boolean(searchValue),
          onFilterChange: (column, filterList, type) => {
            if (['chip', 'reset'].includes(type)) {
              handleFilterSubmit(filterList);
            }
          },
          searchPlaceholder: 'Enter product name',
          customToolbarSelect: (selectedRows, displayData, setSelectedRows) => (
            <CustomToolbarSelect selectedRows={selectedRows} displayData={displayData} setSelectedRows={setSelectedRows} />
          ),
          customSearchRender: (
            searchText,
            handleSearch,
            hideSearch
          ) => {
            return (
              <Grow appear in timeout={300}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={searchValue}
                  onChange={({ target: { value } }) =>
                    setSearchValue(value)
                  }
                  onKeyPress={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      setFilter((f) => ({...f, name: searchValue}));
                    }
                  }}
                  InputProps={{
                    style: {
                      paddingRight: 0,
                    },
                    startAdornment: (
                      <Icon
                        className="mr-2"
                        fontSize="small"
                      >
                        search
                      </Icon>
                    ),
                    endAdornment: (
                      <IconButton
                        onClick={() => {
                          setSearchValue('');
                          setFilter((f) => ({...f, name: ''}));
                          hideSearch();
                        }}
                      >
                        <Icon fontSize="small">
                          clear
                        </Icon>
                      </IconButton>
                    ),
                  }}
                />
              </Grow>
            );
          },
          onTableChange: (action, state) => {
            if (action === 'propsUpdate') {
              state.selectedRows = {
                data: [],
                lookup: {},
              };
            }
          },
          rowsPerPage: filter.limit,
          page: page - 1,
          rowsPerPageOptions: [10, 20, 40, 80, 100],
          serverSide: true,
          count: totalCount,
          onChangePage: (currentPage) => setFilter((f) => ({...f, page: currentPage + 1})),
          onChangeRowsPerPage: (newLimit) => setFilter((f) => ({...f, limit: newLimit, page: 1}))
        }}
      />

      {myLibraryData.map((item, ind) => {
        const isLoadingPromo = !!loadingPromotionsList.find((el) => el === item._id);

        return (
          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            key={ind}
          >
            <Grid item>
              <Dialog
                open={activeTemplate._id == item._id}
                onClose={() => handleCloseDialog()}
                maxWidth="md"
              >
                <div className={classes.modal}>
                  <Grid item md={12} sm={4} xs={6} key={item._id}>
                    <Card elevation="25" container className="p-5" spacing={4}>
                      <Grid item md={12} sm={6} xs={12} className={classes.imageBlock}>
                        {item.slider.bannerImageInfo ?
                          <CardMedia
                            component="img"
                            className={classes.cardImage}
                            height="140"
                            alt="Image not found"
                            image={item.slider.bannerImageInfo.imageS3}
                            title={item.promoName}
                          /> :
                          <CardMedia image={logo} className={classes.cardImage} title={item.promoName}>
                            <div elevation={25} className={classes.cardBackground} />
                          </CardMedia>
                        }
                      </Grid>
                      <Grid item md={12} sm={6} xs={12} className={classes.content}>
                        <Typography gutterBottom variant="h5" component="h2" className="mb-2">
                          {item.promoName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" component="p">
                          <span className="font-semibold">Discount Type: </span>
                          {item.promoType?.typeName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" component="p" className={classes.description} title={item.promoDescription}>
                          <span className="font-semibold">Description: </span>
                        </Typography>
                        <div dangerouslySetInnerHTML={{ __html: md.render(item?.promoDescription || '') }} />
                        {item.salesRules[0] &&
                          <Typography variant="body2" color="textSecondary" component="p">
                            <span className="font-semibold">Discount: </span>
                            {item.salesRules[0]?.discountAmount} {discount_unit[item.salesRules[0]?.discountType]}
                          </Typography>
                        }
                        <Typography color="textSecondary" component="p">
                          <span className="font-semibold">Promoted Listings: </span>
                        </Typography>
                        {item.skusCategories?.length ?
                          item.skusCategories?.map((category) => {
                            return (
                              <Typography key={category.id} color="textSecondary" component="p">
                                {category.name}
                              </Typography>
                            );
                          })
                          :
                          item.skus?.map((product) => {
                            return (
                              <Typography key={product.sku} color="textSecondary" component="p">
                                {product.name}
                              </Typography>
                            );
                          })
                        }
                      </Grid>

                      <Grid item xs={12} className={classes.datesRow}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                          <DatePicker
                            className={classes.dateInput}
                            variant="inline"
                            label="Start Date"
                            disablePast
                            autoOk
                            format="MM/dd/yyyy"
                            value={startDate}
                            onChange={setStartDate}
                          />
                        </MuiPickersUtilsProvider>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                          <DatePicker
                            className={classes.dateInput}
                            error={endDate < startDate}
                            helperText={endDate < startDate && 'End Date cant be less than Start Date'}
                            variant="inline"
                            label="End Date"
                            disablePast
                            autoOk
                            format="MM/dd/yyyy"
                            value={endDate}
                            onChange={setEndDate}
                          />
                        </MuiPickersUtilsProvider>
                        <Grid item md={4} sm={6} xs={12}>
                          <Button
                            variant="contained"
                            color="primary"
                            className={classes.startButton}
                            disabled={activeTemplate?._id === item._id && (endDate < startDate || isLoadingPromo)}
                            onClick={() => handleClickStart(item)}
                          >
                            {isLoadingPromo ? <CircularProgress size={25} /> : 'Publish'}
                          </Button>
                          <Button
                            color="primary"
                            variant="contained"
                            className={classes.startButton}
                            onClick={() => handleCloseDialog()}
                          >
                            Close
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </div>
              </Dialog>
            </Grid>
          </Grid>
        );
      })}
      <ConfirmDialog
        title="Delete product?"
        open={Boolean(templateToDelete)}
        setOpen={setTemplateToDelete}
        onConfirm={() => deleteSingleTemplateAPI({dispatch, id: templateToDelete._id})}
      >
        Are you sure you want to delete the template from your library?
      </ConfirmDialog>
    </div>
  );
}
